import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  LinearProgress,
  Paper,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add,
  Search,
  Campaign,
  Email,
  Sms,
  WhatsApp,
  TrendingUp,
  Refresh,
  Download,
} from '@mui/icons-material';
import DataTable, { Column } from '../components/common/DataTable';
import KPIWidget from '../components/dashboard/KPIWidget';
import BookingLinkCard from '../components/marketing/BookingLinkCard';
import MessageTemplatesTab from '../components/marketing/MessageTemplatesTab';
import { useAppDispatch } from '../hooks/redux';
import { addNotification } from '../store/slices/uiSlice';
import { format } from 'date-fns';
import { campaignService, type CreateCampaignPayload } from '../services/campaignService';
import '../styles/minimal-padding.css';

// Enhanced Metric Card Component with forwardRef
interface MetricCardProps {
  title: string;
  value: string | number;
  change: number;
  changeType: 'increase' | 'decrease';
  bgGradient: string;
  icon: React.ReactNode;
}

const MetricCard = React.forwardRef<HTMLDivElement, MetricCardProps>(({
  title,
  value,
  change,
  changeType,
  bgGradient,
  icon,
}, ref) => (
  <Card
    ref={ref}
    className="metric-card"
    sx={{
      background: bgGradient,
      position: 'relative',
      overflow: 'hidden',
      minHeight: 140,
      borderRadius: 4,
      border: 'none',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
      color: '#FFFFFF',
      cursor: 'pointer',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%)',
        transform: 'translateX(-100%)',
        transition: 'transform 0.6s',
      },
      '&:hover::before': {
        transform: 'translateX(100%)',
      },
    }}
  >
    <CardContent sx={{ position: 'relative', zIndex: 2, p: 3, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.95)',
              textTransform: 'uppercase',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1.5px',
              mb: 1,
              display: 'block',
              textShadow: '0 1px 3px rgba(0,0,0,0.35)',
            }}
          >
            {title}
          </Typography>
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#FFFFFF',
              fontWeight: 800,
              fontSize: { xs: '1.8rem', sm: '2.2rem' },
              lineHeight: 1.2,
              mb: 1,
              textShadow: '0 2px 8px rgba(0,0,0,0.35)',
            }}
          >
            {value}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <TrendingUp sx={{ fontSize: 18, color: '#FFFFFF', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '0.85rem',
                fontWeight: 600,
                textShadow: '0 1px 2px rgba(0,0,0,0.35)',
              }}
            >
              {changeType === 'increase' ? '+' : '-'}{change}%
            </Typography>
          </Box>
        </Box>
        <Avatar 
          sx={{ 
            bgcolor: 'rgba(255, 255, 255, 0.2)', 
            width: 70, 
            height: 70,
            color: 'white',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ fontSize: { xs: 30, sm: 35 } }}>
            {icon}
          </Box>
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));

MetricCard.displayName = 'MetricCard';

interface MarketingCampaign {
  id: string;
  name: string;
  type: 'promotional' | 'reminder' | 'birthday' | 'feedback';
  channel: 'sms' | 'whatsapp' | 'email' | 'push';
  status: 'draft' | 'scheduled' | 'sent' | 'cancelled';
  targetAudience: string;
  scheduledAt: string;
  metrics: {
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    converted: number;
  };
  createdAt: string;
}

function targetAudienceLabel(ta: unknown): string {
  if (ta && typeof ta === 'object' && ta !== null && 'customerSegment' in ta) {
    return String((ta as { customerSegment: string }).customerSegment);
  }
  if (typeof ta === 'string') return ta;
  return 'All';
}

function mapApiCampaign(c: any): MarketingCampaign {
  return {
    id: c.id,
    name: c.name ?? 'Untitled',
    type: (c.type ?? 'promotional').toLowerCase(),
    channel: (c.channel ?? 'email').toLowerCase(),
    status: (c.status ?? 'draft').toLowerCase(),
    targetAudience: targetAudienceLabel(c.targetAudience),
    scheduledAt: c.scheduledAt ?? c.createdAt ?? '',
    metrics: c.metrics ?? { sent: 0, delivered: 0, opened: 0, clicked: 0, converted: 0 },
    createdAt: c.createdAt ?? '',
  };
}

const campaignColumns: Column[] = [
  {
    id: 'name',
    label: 'Campaign Name',
    format: (value: string, row: MarketingCampaign) => (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {row.type.charAt(0).toUpperCase() + row.type.slice(1)}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'channel',
    label: 'Channel',
    format: (value: string) => {
      const getIcon = () => {
        switch (value) {
          case 'email': return <Email fontSize="small" />;
          case 'sms': return <Sms fontSize="small" />;
          case 'whatsapp': return <WhatsApp fontSize="small" />;
          default: return <Campaign fontSize="small" />;
        }
      };
      return (
        <Chip
          icon={getIcon()}
          label={value.toUpperCase()}
          size="small"
          variant="outlined"
        />
      );
    },
  },
  {
    id: 'targetAudience',
    label: 'Target Audience',
  },
  {
    id: 'metrics',
    label: 'Performance',
    format: (_value: any, row: MarketingCampaign) => {
      if (row.status === 'draft' || row.status === 'scheduled') {
        return <Typography variant="body2" color="text.secondary">Not sent yet</Typography>;
      }
      // const deliveryRate = row.metrics.sent > 0 ? (row.metrics.delivered / row.metrics.sent) * 100 : 0;
      const openRate = row.metrics.delivered > 0 ? (row.metrics.opened / row.metrics.delivered) * 100 : 0;
      const conversionRate = row.metrics.opened > 0 ? (row.metrics.converted / row.metrics.opened) * 100 : 0;
      
      return (
        <Box>
          <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
            Delivered: {row.metrics.sent} | Opened: {row.metrics.opened}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Delivered counts successful sends to the provider (not total audience).
          </Typography>
          <Typography variant="caption" color="success.main">
            {openRate.toFixed(1)}% open rate | {conversionRate.toFixed(1)}% conversion
          </Typography>
        </Box>
      );
    },
  },
  {
    id: 'status',
    label: 'Status',
    format: (value: string) => {
      const getColor = () => {
        switch (value) {
          case 'sent': return 'success';
          case 'scheduled': return 'info';
          case 'cancelled': return 'error';
          default: return 'default';
        }
      };
      return (
        <Chip
          label={value.toUpperCase()}
          size="small"
          color={getColor() as any}
        />
      );
    },
  },
  {
    id: 'scheduledAt',
    label: 'Scheduled',
    format: (value: string) => format(new Date(value), 'MMM dd, yyyy HH:mm'),
  },
];

const Marketing: React.FC = () => {
  const dispatch = useAppDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const data = await campaignService.getCampaigns();
      setCampaigns((data ?? []).map(mapApiCampaign));
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    channel: 'SMS' as CreateCampaignPayload['channel'],
    type: 'PROMOTIONAL' as CreateCampaignPayload['type'],
    content: '',
    subject: '',
    scheduledAt: '',
    // WhatsApp Business Cloud API approved template (optional). When set,
    // the backend sends the templated message instead of freeform — required
    // for broadcasts outside the 24h customer-service window.
    templateName: '',
    templateLanguage: 'en',
    templateVariables: '',
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailCampaign, setDetailCampaign] = useState<any>(null);

  const [sendConfirmId, setSendConfirmId] = useState<string | null>(null);
  const [sendLoading, setSendLoading] = useState(false);

  const openCreate = () => {
    const d = new Date();
    d.setHours(d.getHours() + 1);
    setNewCampaign({
      name: '',
      channel: 'SMS',
      type: 'PROMOTIONAL',
      content: '',
      subject: '',
      scheduledAt: d.toISOString().slice(0, 16),
      templateName: '',
      templateLanguage: 'en',
      templateVariables: '',
    });
    setCreateOpen(true);
  };

  const handleCreateSubmit = async () => {
    const usingTemplate =
      newCampaign.channel === 'WHATSAPP' && newCampaign.templateName.trim().length > 0;
    if (!newCampaign.name.trim()) {
      dispatch(addNotification({ type: 'error', message: 'Name is required.' }));
      return;
    }
    if (!usingTemplate && !newCampaign.content.trim()) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'Provide a message body, or pick a WhatsApp template name.',
        })
      );
      return;
    }
    const scheduled = newCampaign.scheduledAt
      ? new Date(newCampaign.scheduledAt).toISOString()
      : new Date().toISOString();
    setCreating(true);
    try {
      const templateVarList = newCampaign.templateVariables
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const payload: CreateCampaignPayload = {
        name: newCampaign.name.trim(),
        type: newCampaign.type,
        channel: newCampaign.channel,
        targetAudience: {
          customerSegment: 'all_active',
          filters: { status: ['ACTIVE'] },
        },
        message: {
          content: newCampaign.content.trim(),
          ...(newCampaign.channel === 'EMAIL' && newCampaign.subject.trim()
            ? { subject: newCampaign.subject.trim() }
            : {}),
          ...(usingTemplate
            ? {
                templateName: newCampaign.templateName.trim(),
                templateLanguage: newCampaign.templateLanguage.trim() || 'en',
                templateVariables: templateVarList,
              }
            : {}),
        } as any,
        scheduledAt: scheduled,
      };
      await campaignService.createCampaign(payload);
      dispatch(addNotification({ type: 'success', message: 'Campaign created.' }));
      setCreateOpen(false);
      await fetchCampaigns();
    } catch (e: unknown) {
      dispatch(
        addNotification({
          type: 'error',
          message: e instanceof Error ? e.message : 'Failed to create campaign',
        })
      );
    } finally {
      setCreating(false);
    }
  };

  const handleViewCampaign = async (row: MarketingCampaign) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailCampaign(null);
    try {
      const c = await campaignService.getCampaignById(row.id);
      setDetailCampaign(c);
    } catch {
      setDetailCampaign({ error: 'Could not load campaign' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSendCampaign = async (id: string) => {
    setSendLoading(true);
    try {
      await campaignService.sendCampaign(id);
      dispatch(addNotification({ type: 'success', message: 'Campaign sent.' }));
      setSendConfirmId(null);
      await fetchCampaigns();
    } catch (e: unknown) {
      dispatch(
        addNotification({
          type: 'error',
          message: e instanceof Error ? e.message : 'Send failed',
        })
      );
    } finally {
      setSendLoading(false);
    }
  };

  const exportCampaignsCsv = () => {
    const headers = ['Name', 'Channel', 'Status', 'Sent', 'Scheduled'];
    const lines = [
      headers.join(','),
      ...campaigns.map((c) =>
        [
          `"${(c.name || '').replace(/"/g, '""')}"`,
          c.channel,
          c.status,
          c.metrics?.sent ?? 0,
          c.scheduledAt ? new Date(c.scheduledAt).toISOString() : '',
        ].join(',')
      ),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campaigns-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getMarketingStats = () => {
    const totalSent = campaigns.reduce((sum, c) => sum + c.metrics.sent, 0);
    const totalOpened = campaigns.reduce((sum, c) => sum + c.metrics.opened, 0);
    const totalConverted = campaigns.reduce((sum, c) => sum + c.metrics.converted, 0);
    const avgOpenRate = totalSent > 0 ? (totalOpened / totalSent) * 100 : 0;
    const avgConversionRate = totalOpened > 0 ? (totalConverted / totalOpened) * 100 : 0;

    return {
      totalCampaigns: campaigns.length,
      totalSent,
      avgOpenRate,
      avgConversionRate,
    };
  };

  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.targetAudience.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = getMarketingStats();

  const upcomingCampaigns = campaigns
    .filter(c => c.status === 'scheduled')
    .sort((a, b) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime())
    .slice(0, 5);

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      {/* Enhanced Header Section with Animation */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 3, 
          background: 'linear-gradient(135deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(232, 180, 184, 0.3)',
          color: '#2D1F22',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          },
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="h3" 
                component="h1" 
                sx={{ 
                  fontWeight: 800, 
                  color: '#2D1F22',
                  mb: 1,
                  letterSpacing: '-0.02em',
                }}
              >
                Marketing & Campaigns
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(45, 31, 34, 0.88)',
                  fontSize: '1.15rem',
                  fontWeight: 500,
                  maxWidth: 520,
                }}
              >
                Engage customers with targeted marketing campaigns
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Refresh Data" arrow>
                <IconButton 
                  onClick={() => void fetchCampaigns()}
                  disabled={loading}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.65)', 
                    color: '#2D1F22',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(45, 31, 34, 0.12)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.92)',
                      color: '#1A1214',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Report" arrow>
                <IconButton 
                  onClick={exportCampaignsCsv}
                  sx={{ 
                    bgcolor: 'rgba(255, 255, 255, 0.65)', 
                    color: '#2D1F22',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(45, 31, 34, 0.12)',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'rgba(255, 255, 255, 0.92)',
                      color: '#1A1214',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openCreate}
                sx={{
                  bgcolor: 'white',
                  color: '#2D3748',
                  fontWeight: 800,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1rem',
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                  border: '2px solid rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                    color: '#1A202C',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.2)',
                    border: '2px solid white',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                Create Campaign
              </Button>
            </Box>
          </Box>
        </Box>
        
        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: -30,
            right: -30,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            left: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
            zIndex: 1,
          }}
        />
      </Paper>

      {/* Stage 1 WhatsApp Business App: shareable booking link + QR */}
      <BookingLinkCard />

      {/* Enhanced Marketing KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={300} style={{ transitionDelay: '100ms' }}>
            <MetricCard
              title="Total Campaigns"
              value={stats.totalCampaigns}
              change={20}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              icon={<Campaign />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={400} style={{ transitionDelay: '200ms' }}>
            <MetricCard
              title="Messages Sent"
              value={stats.totalSent}
              change={15.5}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              icon={<Sms />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={500} style={{ transitionDelay: '300ms' }}>
            <MetricCard
              title="Avg Open Rate"
              value={`${stats.avgOpenRate.toFixed(1)}%`}
              change={8.2}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
              icon={<Email />}
            />
          </Zoom>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Zoom in timeout={600} style={{ transitionDelay: '400ms' }}>
            <MetricCard
              title="Conversion Rate"
              value={`${stats.avgConversionRate.toFixed(1)}%`}
              change={12.3}
              changeType="increase"
              bgGradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
              icon={<TrendingUp />}
            />
          </Zoom>
        </Grid>
      </Grid>

      <Fade in timeout={700} style={{ transitionDelay: '500ms' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card 
              sx={{ 
                borderRadius: 4,
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(232, 180, 184, 0.2)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #E8B4B8 0%, #F5D5D8 50%, #E8B4B8 100%)',
                },
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '1rem',
                      minHeight: 64,
                      color: 'text.secondary',
                      '&.Mui-selected': {
                        color: '#7A3D47',
                        fontWeight: 700,
                      },
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#9E4E5B',
                      height: 3,
                    },
                  }}
                >
                  <Tab label="All Campaigns" />
                  <Tab label="Templates" />
                  <Tab label="Automation" />
                </Tabs>
              </Box>

            <Box sx={{ p: 3 }}>
              {tabValue === 0 && (
                <Box>
                  {/* Enhanced Search */}
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      mb: 3, 
                      bgcolor: 'rgba(232, 180, 184, 0.05)',
                      borderRadius: 3,
                      border: '1px solid rgba(232, 180, 184, 0.1)',
                    }}
                  >
                    <TextField
                      fullWidth
                      placeholder="Search campaigns..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ color: '#9E4E5B' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          bgcolor: 'white',
                          '&:hover fieldset': {
                            borderColor: '#9E4E5B',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#7A3D47',
                          },
                        },
                      }}
                    />
                  </Paper>

                  {/* Enhanced Campaigns Table */}
                  <DataTable
                    columns={campaignColumns}
                    rows={filteredCampaigns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalCount={filteredCampaigns.length}
                    onPageChange={setPage}
                    onRowsPerPageChange={setRowsPerPage}
                    loading={loading}
                    onView={(campaign) => void handleViewCampaign(campaign)}
                    onSend={(campaign) => setSendConfirmId(campaign.id)}
                    onDelete={(campaign) => {
                      void (async () => {
                        try {
                          await campaignService.deleteCampaign(campaign.id);
                          dispatch(
                            addNotification({
                              type: 'success',
                              message: `Campaign "${campaign.name}" deleted`,
                            })
                          );
                          await fetchCampaigns();
                        } catch (e: unknown) {
                          dispatch(
                            addNotification({
                              type: 'error',
                              message: e instanceof Error ? e.message : 'Delete failed',
                            })
                          );
                        }
                      })();
                    }}
                  />
                </Box>
              )}

              {tabValue === 1 && (
                <MessageTemplatesTab />
              )}

              {tabValue === 2 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: '#8B5CF6',
                        color: '#FFFFFF',
                        width: 48, 
                        height: 48,
                        boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                      }}
                    >
                      <Campaign />
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: '#2D1F22', letterSpacing: '-0.02em' }}>
                        Marketing Automation
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(45, 31, 34, 0.92)', fontWeight: 500, mt: 0.5, maxWidth: 560, lineHeight: 1.5 }}>
                        Example flows below. Turn on real automations and rules under Settings → Marketing.
                      </Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card 
                        sx={{ 
                          borderRadius: 3,
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(232, 180, 184, 0.1)',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: '#10B981', width: 40, height: 40 }}>
                              👋
                            </Avatar>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.35 }}>
                              Welcome Series
                            </Typography>
                          </Box>
                          <Typography variant="body2" paragraph sx={{ color: '#2D1F22', lineHeight: 1.6 }}>
                            Automatically send welcome messages to new customers with service information.
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>Status:</Typography>
                            <Chip label="Active" color="success" size="small" sx={{ fontWeight: 600 }} />
                          </Box>
                          <Box sx={{ mb: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={85} 
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'rgba(16, 185, 129, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: '#10B981',
                                  borderRadius: 4,
                                },
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: 'rgba(45, 31, 34, 0.88)', fontWeight: 600, display: 'block', mt: 0.5 }}>
                            85% completion rate
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card 
                        sx={{ 
                          borderRadius: 3,
                          background: 'rgba(255, 255, 255, 0.8)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(232, 180, 184, 0.1)',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                          },
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: '#F97316', width: 40, height: 40 }}>
                              🔄
                            </Avatar>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.35 }}>
                              Re-engagement Campaign
                            </Typography>
                          </Box>
                          <Typography variant="body2" paragraph sx={{ color: '#2D1F22', lineHeight: 1.6 }}>
                            Target customers who haven't visited in the last 90 days with special offers.
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>Status:</Typography>
                            <Chip label="Active" color="success" size="small" sx={{ fontWeight: 600 }} />
                          </Box>
                          <Box sx={{ mb: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={65} 
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'rgba(249, 115, 22, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: '#F97316',
                                  borderRadius: 4,
                                },
                              }}
                            />
                          </Box>
                          <Typography variant="caption" sx={{ color: 'rgba(45, 31, 34, 0.88)', fontWeight: 600, display: 'block', mt: 0.5 }}>
                            65% completion rate
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(232, 180, 184, 0.2)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: '#667eea', 
                    width: 48, 
                    height: 48,
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  }}
                >
                  <Campaign />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Upcoming Campaigns
                </Typography>
              </Box>
              {upcomingCampaigns.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    No upcoming campaigns scheduled
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {upcomingCampaigns.map((campaign) => (
                    <ListItem 
                      key={campaign.id} 
                      sx={{ 
                        px: 0, 
                        py: 2,
                        borderRadius: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(232, 180, 184, 0.08)',
                          transform: 'translateX(4px)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#E8B4B8', width: 40, height: 40 }}>
                          <Campaign />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
                            {campaign.name}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {format(new Date(campaign.scheduledAt), 'MMM dd, yyyy HH:mm')}
                            </Typography>
                            <Chip
                              label={campaign.channel.toUpperCase()}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                height: 22,
                                borderColor: '#9E4E5B',
                                color: '#6B333C',
                                fontWeight: 600,
                                bgcolor: 'rgba(158, 78, 91, 0.06)',
                              }}
                            />
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          <Card 
            sx={{ 
              mt: 3,
              borderRadius: 4,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(232, 180, 184, 0.2)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar 
                  sx={{ 
                    bgcolor: '#4facfe', 
                    width: 48, 
                    height: 48,
                    boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)',
                  }}
                >
                  <TrendingUp />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Channel Performance
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatsApp sx={{ fontSize: 18, color: '#25D366' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        WhatsApp
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#25D366' }}>78%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={78} 
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'rgba(37, 211, 102, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#25D366',
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Sms sx={{ fontSize: 18, color: '#06B6D4' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        SMS
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#06B6D4' }}>65%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={65} 
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'rgba(6, 182, 212, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#06B6D4',
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email sx={{ fontSize: 18, color: '#F97316' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        Email
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#F97316' }}>45%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={45} 
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'rgba(249, 115, 22, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: '#F97316',
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Fade>

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'text.primary', fontWeight: 700 }}>Create campaign</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Name"
            value={newCampaign.name}
            onChange={(e) => setNewCampaign((p) => ({ ...p, name: e.target.value }))}
            fullWidth
            required
          />
          <FormControl fullWidth>
            <InputLabel>Channel</InputLabel>
            <Select
              label="Channel"
              value={newCampaign.channel}
              onChange={(e) =>
                setNewCampaign((p) => ({
                  ...p,
                  channel: e.target.value as CreateCampaignPayload['channel'],
                }))
              }
            >
              <MenuItem value="SMS">SMS</MenuItem>
              <MenuItem value="WHATSAPP">WhatsApp</MenuItem>
              <MenuItem value="EMAIL">Email</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={newCampaign.type}
              onChange={(e) =>
                setNewCampaign((p) => ({
                  ...p,
                  type: e.target.value as CreateCampaignPayload['type'],
                }))
              }
            >
              <MenuItem value="PROMOTIONAL">Promotional</MenuItem>
              <MenuItem value="REMINDER">Reminder</MenuItem>
              <MenuItem value="BIRTHDAY">Birthday</MenuItem>
              <MenuItem value="FEEDBACK">Feedback</MenuItem>
            </Select>
          </FormControl>
          {newCampaign.channel === 'EMAIL' && (
            <TextField
              label="Subject"
              value={newCampaign.subject}
              onChange={(e) => setNewCampaign((p) => ({ ...p, subject: e.target.value }))}
              fullWidth
            />
          )}

          {newCampaign.channel === 'WHATSAPP' && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid rgba(37,211,102,0.25)',
                bgcolor: 'rgba(37,211,102,0.05)',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, color: '#1FB957' }}>
                Use an approved WhatsApp template (optional)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Leave blank to send freeform — works only for customers who messaged you in the
                last 24h. Fill in to broadcast outside that window.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  label="Template name"
                  value={newCampaign.templateName}
                  onChange={(e) =>
                    setNewCampaign((p) => ({ ...p, templateName: e.target.value }))
                  }
                  placeholder="e.g. appointment_reminder"
                  size="small"
                  sx={{ flex: '1 1 220px' }}
                />
                <TextField
                  label="Language"
                  value={newCampaign.templateLanguage}
                  onChange={(e) =>
                    setNewCampaign((p) => ({ ...p, templateLanguage: e.target.value }))
                  }
                  placeholder="en"
                  size="small"
                  sx={{ width: 100 }}
                />
              </Box>
              <TextField
                label="Variables (comma-separated)"
                value={newCampaign.templateVariables}
                onChange={(e) =>
                  setNewCampaign((p) => ({ ...p, templateVariables: e.target.value }))
                }
                placeholder="{{firstName}}, 5 PM"
                size="small"
                fullWidth
                sx={{ mt: 1.5 }}
                helperText="Each value fills one {{N}} placeholder in your template, in order. {{firstName}} / {{lastName}} / {{salonName}} are merged per-customer."
              />
            </Box>
          )}

          <TextField
            label={
              newCampaign.channel === 'WHATSAPP' && newCampaign.templateName.trim()
                ? 'Fallback message (used only if WhatsApp falls back to legacy sender)'
                : 'Message'
            }
            value={newCampaign.content}
            onChange={(e) => setNewCampaign((p) => ({ ...p, content: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
            required={
              !(newCampaign.channel === 'WHATSAPP' && newCampaign.templateName.trim())
            }
            placeholder="Hi {{firstName}}, …"
          />
          <TextField
            label="Scheduled"
            type="datetime-local"
            value={newCampaign.scheduledAt}
            onChange={(e) => setNewCampaign((p) => ({ ...p, scheduledAt: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" color="text.secondary">
            Audience: active customers only. Use Settings → Marketing for channel permissions and automation toggles.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void handleCreateSubmit()} disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'text.primary', fontWeight: 700 }}>Campaign details</DialogTitle>
        <DialogContent>
          {detailLoading && (
            <Typography variant="body2" color="text.secondary">
              Loading…
            </Typography>
          )}
          {!detailLoading && detailCampaign && !detailCampaign.error && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {detailCampaign.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Status:
                </Box>{' '}
                {detailCampaign.status}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  Channel:
                </Box>{' '}
                {detailCampaign.channel}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                {typeof detailCampaign.message === 'object' && detailCampaign.message !== null
                  ? JSON.stringify(detailCampaign.message, null, 2)
                  : String(detailCampaign.message ?? '')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Metrics: {JSON.stringify(detailCampaign.metrics ?? {})}
              </Typography>
            </Box>
          )}
          {!detailLoading && detailCampaign?.error && (
            <Typography color="error">{detailCampaign.error}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!sendConfirmId} onClose={() => !sendLoading && setSendConfirmId(null)}>
        <DialogTitle sx={{ color: 'text.primary', fontWeight: 700 }}>Send campaign?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            This will send messages to all customers in the campaign segment (subject to channel config and opt-outs).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendConfirmId(null)} disabled={sendLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={sendLoading || !sendConfirmId}
            onClick={() => sendConfirmId && void handleSendCampaign(sendConfirmId)}
          >
            {sendLoading ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Marketing;